import { Link, useNavigate } from "react-router-dom";
import { Search, LogIn, User, LogOut, UserCircle, Menu, BarChart3, Gift, FileText, Route, Sun, Moon, Users, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { useState, useEffect } from "react";
import supabase from "./../utils/supabaseClient";
import { useTheme } from "./theme-provider";

interface Session {
    provider_token?: string | null
    provider_refresh_token?: string | null
    access_token: string
    refresh_token: string
    expires_in: number
    expires_at?: number
    token_type: string
    user: User
}

interface User {
    id: string
    aud: string
    email?: string
    role?: string
}

export const Navigation = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [session, setSession] = useState<Session | null>(() => {
        const cachedSession = localStorage.getItem('session');
        return cachedSession ? JSON.parse(cachedSession) : null;
    });
    const [isOpen, setIsOpen] = useState(false);
    const { theme, setTheme } = useTheme();

    useEffect(() => {
        const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
        });

        return () => {
            authListener.subscription.unsubscribe();
        };
    }, []);

    const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (searchTerm.trim()) {
                navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
            }
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        setIsOpen(false);
        localStorage.removeItem("session");
        navigate('/login');
    };

    const handleNavigation = (path: string) => {
        setIsOpen(false);
        navigate(path);
    };

    return (
        <nav className="fixed top-0 left-0 right-0 bg-background/80 backdrop-blur-md z-50 border-b border-border">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center h-16">
                    <div className="flex items-center">
                        <Link to="/" className="font-garet font-bold text-foreground md:text-xl shrink-0">
                            <span className="hidden md:inline">
                                <img src="/lovable-uploads/ce0bf2e8-572b-41e8-9136-a406a4885575.png" alt="blankpoll" className="h-8" />
                            </span>
                            <span className="md:hidden text-2xl">
                                <span className="text-[#A6B49E]">_</span>b:p
                            </span>
                        </Link>
                        <div className="hidden md:flex items-center space-x-6 ml-6">
                            <Link to="/polls" className="text-muted-foreground hover:text-foreground transition-colors">
                                Polls
                            </Link>
                            <Link to="/leaderboard" className="text-muted-foreground hover:text-foreground transition-colors">
                                Board
                            </Link>
                            <Link to="/benefits" className="text-muted-foreground hover:text-foreground transition-colors">
                                Benefits
                            </Link>
                            <Link to="/clans" className="text-muted-foreground hover:text-foreground transition-colors">
                                Clans
                            </Link>
                        </div>
                    </div>

                    <div className="hidden sm:flex flex-1 justify-end items-center">
                        <div className="relative w-64 mr-2">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4"/>
                            <input
                                type="text"
                                placeholder="Search polls..."
                                className="pl-10 pr-3 py-1.5 rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-ring/20 w-full text-sm bg-background"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleSearch}
                            />
                        </div>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="ml-2">
                                    {theme === "light" ? (
                                        <Sun className="h-4 w-4" />
                                    ) : (
                                        <Moon className="h-4 w-4" />
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setTheme("light")} className="flex items-center gap-2">
                                    <Sun className="h-4 w-4" />
                                    Light
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setTheme("dark")} className="flex items-center gap-2">
                                    <Moon className="h-4 w-4" />
                                    Dark
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <div className="relative flex-1 flex justify-center sm:hidden">
                        <div className="relative w-[85%]">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4"/>
                            <input
                                type="text"
                                placeholder="Search polls..."
                                className="pl-10 pr-3 py-1.5 rounded-md border border-input focus:outline-none focus:ring-2 focus:ring-ring/20 w-full text-sm bg-background"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleSearch}
                            />
                        </div>
                    </div>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="sm:hidden ml-2">
                                {theme === "light" ? (
                                    <Sun className="h-4 w-4" />
                                ) : (
                                    <Moon className="h-4 w-4" />
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setTheme("light")} className="flex items-center gap-2">
                                <Sun className="h-4 w-4" />
                                Light
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setTheme("dark")} className="flex items-center gap-2">
                                <Moon className="h-4 w-4" />
                                Dark
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <div className="hidden md:flex items-center shrink-0 ml-2">
                        {session ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                        <User className="h-4 w-4"/>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48 bg-white dark:bg-gray-800">
                                    <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:bg-gray-700">
                                        <UserCircle className="h-4 w-4 mr-2"/>
                                        Account
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:bg-gray-700">
                                        <LogOut className="h-4 w-4 mr-2"/>
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="hidden md:flex items-center space-x-2 ml-2">
                                <Button
                                    variant="ghost"
                                    className="flex items-center space-x-2 dark:hover:text-black"
                                    onClick={() => navigate('/login')}
                                >
                                    <LogIn className="h-4 w-4"/>
                                    <span>Login</span>
                                </Button>
                                <Button
                                    className="bg-[#E5F4EA] text-[#155231] hover:bg-[#D5E8DC]"
                                    onClick={() => navigate('/signup')}
                                >
                                    Sign Up
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="md:hidden ml-2">
                        <Sheet open={isOpen} onOpenChange={setIsOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <Menu className="h-5 w-5"/>
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-full sm:w-80">
                                <SheetHeader>
                                    <SheetTitle>Menu</SheetTitle>
                                </SheetHeader>
                                <div className="flex flex-col gap-4 mt-6">
                                    {session ? (
                                        <>
                                            <Button
                                                variant="ghost"
                                                className="w-full justify-start"
                                                onClick={() => handleNavigation('/account')}
                                            >
                                                <UserCircle className="h-4 w-4 mr-2"/>
                                                Account
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                className="w-full justify-start"
                                                onClick={handleLogout}
                                            >
                                                <LogOut className="h-4 w-4 mr-2"/>
                                                Logout
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                variant="ghost"
                                                className="w-full justify-start"
                                                onClick={() => handleNavigation('/login')}
                                            >
                                                <LogIn className="h-4 w-4 mr-2"/>
                                                Login
                                            </Button>
                                            <Button
                                                variant="default"
                                                className="w-full justify-start bg-[#6DA135] text-[#344F16] hover:bg-[#5A8A2D]"
                                                onClick={() => handleNavigation('/signup')}
                                            >
                                                <UserCircle className="h-4 w-4 mr-2"/>
                                                Sign Up
                                            </Button>
                                        </>
                                    )}

                                    <Separator className="my-2" />

                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start"
                                        onClick={() => handleNavigation('/polls')}
                                    >
                                        <BarChart3 className="h-4 w-4 mr-2"/>
                                        Polls
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start"
                                        onClick={() => handleNavigation('/leaderboard')}
                                    >
                                        <Trophy className="h-4 w-4 mr-2"/>
                                        Board
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start"
                                        onClick={() => handleNavigation('/clans')}
                                    >
                                        <Users className="h-4 w-4 mr-2"/>
                                        Clans
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start"
                                        onClick={() => handleNavigation('/leagues')}
                                    >
                                        <Trophy className="h-4 w-4 mr-2"/>
                                        Leagues
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start"
                                        onClick={() => handleNavigation('/benefits')}
                                    >
                                        <Gift className="h-4 w-4 mr-2"/>
                                        Benefits
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start"
                                        onClick={() => handleNavigation('/whitepaper')}
                                    >
                                        <FileText className="h-4 w-4 mr-2"/>
                                        Whitepaper
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start"
                                        onClick={() => handleNavigation('/roadmap')}
                                    >
                                        <Route className="h-4 w-4 mr-2"/>
                                        Roadmap
                                    </Button>

                                    <Separator className="my-2" />
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start"
                                        onClick={() => setTheme(theme === "light" ? "dark" : "light")}
                                    >
                                        {theme === "light" ? (
                                            <>
                                                <Moon className="h-4 w-4 mr-2"/>
                                                Switch to Dark Mode
                                            </>
                                        ) : (
                                            <>
                                                <Sun className="h-4 w-4 mr-2"/>
                                                Switch to Light Mode
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </nav>
    );
};
